import { useCallback, useEffect, useRef, useState } from 'react';

import { apiService } from '../services/api.services';
import type {
  DrfPortalTermDefinition,
  DrfTermContent,
  DrfTermContentState,
  DrfTermsFailureContext,
  UseDrfTermsReturn,
} from '../utils/drfTermsUtil';
import {
  DRF_LIST_PAGINATION,
  DRF_LOAD_DATA_ERROR_MESSAGE,
  DRF_MAX_ATTEMPTS,
  DRF_PERSON_TYPE,
  DRF_REQUEST_TIMEOUT_MESSAGE,
  DRF_REQUEST_TIMEOUT_MS,
  getLatestTermVersion,
  isVerifyAcceptanceSatisfied,
  resolveDrfPersonId,
  withRequestTimeout,
  withRetry,
} from '../utils/drfTermsUtil';
import { SKIP_DRF } from '../constantsConfig/constantsConfig';

const DRF_PORTAL_TERM_ASSOCIATIONS = {
  USAGE: 'CADPJornadaDoEstipulante_003',
  ADHESION: 'CADPJornadaDoEstipulante_002',
  PRIVACY: 'CADPJornadaDoEstipulante_001',
} as const;

const DRF_PORTAL_TERM_DEFINITIONS: DrfPortalTermDefinition[] = [
  {
    id: 'termo-uso',
    title: 'Termo de Uso',
    associationId: DRF_PORTAL_TERM_ASSOCIATIONS.USAGE,
  },
  {
    id: 'termo-adesao',
    title: 'Termo de Adesão',
    associationId: DRF_PORTAL_TERM_ASSOCIATIONS.ADHESION,
  },
  {
    id: 'aviso-privacidade',
    title: 'Aviso de Privacidade',
    associationId: DRF_PORTAL_TERM_ASSOCIATIONS.PRIVACY,
  },
];

const DRF_ALL_ASSOCIATION_IDS = DRF_PORTAL_TERM_DEFINITIONS.map((term) => term.associationId);
const LIST_TERMS_GATE_MAX_ATTEMPTS = 2;

export function useDrfTerms(personCpf?: string | null): UseDrfTermsReturn {
  const personId = resolveDrfPersonId(personCpf);
  const termVersionsRef = useRef<Map<string, number>>(new Map());
  const versionFetchPromisesRef = useRef<Map<string, Promise<number>>>(new Map());
  const pendingAssociationsRef = useRef<string[]>([]);
  const loadingTabsRef = useRef<Set<string>>(new Set());
  const failureContextRef = useRef<DrfTermsFailureContext>(null);
  const modalInitializedRef = useRef(false);
  const loadTermTabContentRef = useRef<(tabId: string, forceReload?: boolean) => Promise<void>>(
    async () => {},
  );

  const [termsCheckLoading, setTermsCheckLoading] = useState(false);
  const [showDrfTermModal, setShowDrfTermModal] = useState(false);
  const [showDrfListTermsFailure, setShowDrfListTermsFailure] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [failureContext, setFailureContext] = useState<DrfTermsFailureContext>(null);
  const [termContentByTab, setTermContentByTab] = useState<Record<string, DrfTermContentState>>({});
  const termContentByTabRef = useRef(termContentByTab);
  termContentByTabRef.current = termContentByTab;
  const [viewedTabIds, setViewedTabIds] = useState<Set<string>>(new Set());

  const allTabsViewed = DRF_PORTAL_TERM_DEFINITIONS.every((term) => viewedTabIds.has(term.id));

  const clearVersionCache = useCallback((associationId?: string) => {
    if (associationId) {
      termVersionsRef.current.delete(associationId);
      versionFetchPromisesRef.current.delete(associationId);
      return;
    }
    termVersionsRef.current.clear();
    versionFetchPromisesRef.current.clear();
  }, []);

  const setFailure = useCallback((context: DrfTermsFailureContext, message: string | null) => {
    failureContextRef.current = context;
    setFailureContext(context);
    setSaveError(message);
  }, []);

  const clearFailure = useCallback(() => {
    failureContextRef.current = null;
    setFailureContext(null);
    setSaveError(null);
    modalInitializedRef.current = false;
  }, []);

  const fetchLatestTermVersion = useCallback(async (associationId: string): Promise<number> => {
    const cachedVersion = termVersionsRef.current.get(associationId);
    if (cachedVersion != null) {
      return cachedVersion;
    }

    const inFlight = versionFetchPromisesRef.current.get(associationId);
    if (inFlight) {
      return inFlight;
    }

    const fetchPromise = (async () => {
      const response = await withRequestTimeout(
        apiService.drf.listTermsByIdentifier({
          idAssociacaoTermoAplicacao: associationId,
          paginacao: { ...DRF_LIST_PAGINATION },
        }),
      );

      const latestVersion = getLatestTermVersion(response.termos ?? []);
      if (latestVersion <= 0) {
        throw new Error(DRF_LOAD_DATA_ERROR_MESSAGE);
      }

      termVersionsRef.current.set(associationId, latestVersion);
      return latestVersion;
    })().finally(() => {
      versionFetchPromisesRef.current.delete(associationId);
    });

    versionFetchPromisesRef.current.set(associationId, fetchPromise);
    return fetchPromise;
  }, []);

  const fetchTermContentByAssociation = useCallback(
    async (associationId: string): Promise<DrfTermContent> => {
      return withRetry(async () => {
        const version = await fetchLatestTermVersion(associationId);
        const response = await withRequestTimeout(
          apiService.drf.getTermByIdentifier({
            idAssociacaoTermoAplicacao: associationId,
            versao: version,
          }),
        );

        const term = response.termo;
        const definition = DRF_PORTAL_TERM_DEFINITIONS.find(
          (item) => item.associationId === associationId,
        );

        return {
          title: term.nome || definition?.title || 'Termos de uso',
          description: term.descricao || '',
          updatedAt: term.dataInicioVigencia,
        };
      }, DRF_MAX_ATTEMPTS);
    },
    [fetchLatestTermVersion],
  );

  const loadTermTabContent = useCallback(
    async (tabId: string, forceReload = false) => {
      const definition = DRF_PORTAL_TERM_DEFINITIONS.find((term) => term.id === tabId);
      if (!definition) {
        return;
      }

      if (!forceReload && loadingTabsRef.current.has(tabId)) {
        return;
      }

      if (!forceReload) {
        const currentState = termContentByTabRef.current[tabId];
        if (
          currentState?.status === 'success' ||
          currentState?.status === 'loading' ||
          currentState?.status === 'error'
        ) {
          return;
        }
      }

      if (forceReload) {
        clearVersionCache(definition.associationId);
      }

      loadingTabsRef.current.add(tabId);
      const loadingState: DrfTermContentState = { status: 'loading' };
      termContentByTabRef.current = {
        ...termContentByTabRef.current,
        [tabId]: loadingState,
      };
      setTermContentByTab((current) => ({
        ...current,
        [tabId]: loadingState,
      }));

      try {
        const content = await fetchTermContentByAssociation(definition.associationId);
        const successState: DrfTermContentState = { status: 'success', content };
        termContentByTabRef.current = {
          ...termContentByTabRef.current,
          [tabId]: successState,
        };
        setTermContentByTab((current) => ({
          ...current,
          [tabId]: successState,
        }));
      } catch {
        const errorState: DrfTermContentState = { status: 'error', message: DRF_LOAD_DATA_ERROR_MESSAGE };
        termContentByTabRef.current = {
          ...termContentByTabRef.current,
          [tabId]: errorState,
        };
        setTermContentByTab((current) => ({
          ...current,
          [tabId]: errorState,
        }));
      } finally {
        loadingTabsRef.current.delete(tabId);
      }
    },
    [clearVersionCache, fetchTermContentByAssociation],
  );

  loadTermTabContentRef.current = loadTermTabContent;

  const selectTermTab = useCallback((tabId: string) => {
    setViewedTabIds((current) => {
      if (current.has(tabId)) {
        return current;
      }
      const next = new Set(current);
      next.add(tabId);
      return next;
    });

    void loadTermTabContentRef.current(tabId);
  }, []);

  const resetModalState = useCallback(() => {
    loadingTabsRef.current.clear();
    modalInitializedRef.current = false;
    setTermContentByTab({});
    setViewedTabIds(new Set());
  }, []);

  useEffect(() => {
    if (!showDrfTermModal) {
      resetModalState();
      return;
    }

    if (modalInitializedRef.current) {
      return;
    }

    const defaultTab = DRF_PORTAL_TERM_DEFINITIONS[0];
    if (!defaultTab) {
      return;
    }

    modalInitializedRef.current = true;
    setViewedTabIds(new Set([defaultTab.id]));
    void loadTermTabContentRef.current(defaultTab.id);
  }, [failureContext, resetModalState, showDrfTermModal]);

  const isAssociationAccepted = useCallback(
    async (associationId: string, version: number): Promise<boolean> => {
      if (!personId) {
        return false;
      }

      try {
        const response = await withRequestTimeout(
          apiService.drf.verifyAcceptance({
            idPessoaFisicaJuridica: personId,
            tipoPessoa: DRF_PERSON_TYPE,
            idAssociacaoTermoAplicacao: associationId,
            versaoTermo: version,
          }),
        );

        return isVerifyAcceptanceSatisfied(response.termo);
      } catch {
        return false;
      }
    },
    [personId],
  );

  const fetchGateTermVersions = useCallback(async (): Promise<
    { associationId: string; version: number }[]
  > => {
    let lastError: unknown;

    for (let attempt = 1; attempt <= LIST_TERMS_GATE_MAX_ATTEMPTS; attempt += 1) {
      if (attempt > 1) {
        clearVersionCache();
      }

      try {
        const versionEntries: { associationId: string; version: number }[] = [];
        for (const associationId of DRF_ALL_ASSOCIATION_IDS) {
          const version = await withRequestTimeout(fetchLatestTermVersion(associationId));
          versionEntries.push({ associationId, version });
        }
        return versionEntries;
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError instanceof Error ? lastError : new Error(DRF_LOAD_DATA_ERROR_MESSAGE);
  }, [clearVersionCache, fetchLatestTermVersion]);

  const showListTermsFailureScreen = useCallback(() => {
    pendingAssociationsRef.current = [];
    setShowDrfTermModal(false);
    setShowDrfListTermsFailure(true);
    clearFailure();
    resetModalState();
  }, [clearFailure, resetModalState]);

  const abandonDrfTerms = useCallback(() => {
    setShowDrfListTermsFailure(false);
    setShowDrfTermModal(false);
    pendingAssociationsRef.current = [];
    clearFailure();
    resetModalState();
  }, [clearFailure, resetModalState]);

  const completeTermsGateAfterVersions = useCallback(
    async (versionEntries: { associationId: string; version: number }[]): Promise<boolean> => {
      const acceptanceResults = await withRequestTimeout(
        Promise.all(
          versionEntries.map(async ({ associationId, version }) => {
            const accepted = await isAssociationAccepted(associationId, version);
            return { associationId, accepted };
          }),
        ),
      );

      const pendingAssociations = acceptanceResults
        .filter((result) => !result.accepted)
        .map((result) => result.associationId);

      if (pendingAssociations.length === 0) {
        setShowDrfTermModal(false);
        setShowDrfListTermsFailure(false);
        pendingAssociationsRef.current = [];
        return true;
      }

      pendingAssociationsRef.current = pendingAssociations;
      setShowDrfListTermsFailure(false);
      setShowDrfTermModal(true);
      return false;
    },
    [isAssociationAccepted],
  );

  const runTermsGate = useCallback(async (): Promise<boolean> => {
    if (SKIP_DRF || !personId) {
      return true;
    }

    setTermsCheckLoading(true);
    clearFailure();
    setShowDrfListTermsFailure(false);

    try {
      let versionEntries: { associationId: string; version: number }[];
      try {
        versionEntries = await fetchGateTermVersions();
      } catch {
        showListTermsFailureScreen();
        return false;
      }

      return await completeTermsGateAfterVersions(versionEntries);
    } catch {
      showListTermsFailureScreen();
      return false;
    } finally {
      setTermsCheckLoading(false);
    }
  }, [
    clearFailure,
    completeTermsGateAfterVersions,
    fetchGateTermVersions,
    personId,
    showListTermsFailureScreen,
  ]);

  const savePendingTerms = useCallback(async (): Promise<boolean> => {
    if (SKIP_DRF) {
      return true;
    }

    if (!personId) {
      setFailure('save', DRF_LOAD_DATA_ERROR_MESSAGE);
      return false;
    }

    const associationsToSave =
      pendingAssociationsRef.current.length > 0
        ? pendingAssociationsRef.current
        : [...DRF_ALL_ASSOCIATION_IDS];

    setSaveLoading(true);
    clearFailure();

    try {
      await withRetry(
        () =>
          withRequestTimeout(
            Promise.all(
              associationsToSave.map(async (associationId) => {
                const version = await fetchLatestTermVersion(associationId);
                return apiService.drf.includeAcceptance({
                  idPessoaFisicaJuridica: personId,
                  tipoPessoa: DRF_PERSON_TYPE,
                  idAssociacaoTermoAplicacao: associationId,
                  versaoTermo: String(version),
                });
              }),
            ),
            DRF_REQUEST_TIMEOUT_MS,
            DRF_REQUEST_TIMEOUT_MESSAGE,
          ),
        DRF_MAX_ATTEMPTS,
      );

      pendingAssociationsRef.current = [];
      setShowDrfTermModal(false);
      return true;
    } catch {
      setFailure('save', DRF_LOAD_DATA_ERROR_MESSAGE);
      return false;
    } finally {
      setSaveLoading(false);
    }
  }, [clearFailure, fetchLatestTermVersion, personId, setFailure]);

  const retryLastAction = useCallback(async (): Promise<boolean> => {
    if (SKIP_DRF) {
      return true;
    }

    if (failureContextRef.current === 'save') {
      return savePendingTerms();
    }

    const erroredTab = DRF_PORTAL_TERM_DEFINITIONS.find(
      (term) => termContentByTabRef.current[term.id]?.status === 'error',
    );
    if (erroredTab) {
      clearVersionCache(erroredTab.associationId);
      await loadTermTabContentRef.current(erroredTab.id, true);
      const stillErrored = termContentByTabRef.current[erroredTab.id]?.status === 'error';
      if (stillErrored) {
        showListTermsFailureScreen();
        return false;
      }
    }

    return false;
  }, [clearVersionCache, savePendingTerms, showListTermsFailureScreen]);

  return {
    termsCheckLoading,
    showDrfTermModal,
    showDrfListTermsFailure,
    saveLoading,
    saveError,
    failureContext,
    termContentByTab,
    allTabsViewed,
    portalTermDefinitions: DRF_PORTAL_TERM_DEFINITIONS,
    skipDrf: SKIP_DRF,
    runTermsGate,
    selectTermTab,
    savePendingTerms,
    retryLastAction,
    abandonDrfTerms,
  };
}
